// sonar.cpd.exclusions

import { getSDKConfig } from '../../shares/lib/getSDKConfig';
import getElementSelectors from '../../shares/lib/getElementSelectors';
import { getSizeOfDevice } from '../../shares/lib/getSizeOfDevice';
import { LogiSDKConfig } from '../../shares/model/config';
import { isTenantFeatureEnabled } from '../../shares/helpers/tenantFeatureHelper';
import { TenantFeature } from '../../shares/enums/tenantFeatures';
import { SwipeCommonMethods, ScreenProps } from './swipeCommon';
import { idleCollapse } from './idleCollapse';

export class SwipeHandler {
  private swipeSDKConfig: LogiSDKConfig;
  private swipe_control: HTMLElement | null;
  private swipeHandle: HTMLElement | null;
  private swipeErrorMsg: HTMLElement | null;
  private swipeSdkContainer: HTMLElement | null;
  private pretX: number;
  private pretY: number;
  private dragStartTop: number;
  private startClickPosY: number;
  private startClickPosX: number;
  private screenProps: ScreenProps;
  private iconPosition: string;
  private iconPositionClass: string;
  private isClickEvent: boolean;
  private currentY: number;
  private readonly isArrowIconsFeature: boolean;
  private isTransitioning: boolean;
  private lastTapTime: number;
  private tapTimeout: NodeJS.Timeout | null;

  constructor() {
    const { swipe_control, swipeHandle, swipeErrorMsg, swipeSdkContainer } =
      getElementSelectors();
    this.swipeSDKConfig = getSDKConfig();
    this.swipe_control = swipe_control;
    this.swipeHandle = swipeHandle;
    this.swipeErrorMsg = swipeErrorMsg;
    this.swipeSdkContainer = swipeSdkContainer;

    const iconTopPercent = this.swipeSDKConfig?.swipeGames?.styles.iconTop ?? 0;
    const iconTopPixels = (globalThis.innerHeight * iconTopPercent) / 100;

    this.pretX = 0;
    this.pretY = iconTopPixels;
    this.dragStartTop = 0;
    this.startClickPosY = 0;
    this.startClickPosX = 0;
    this.screenProps = getSizeOfDevice();
    this.iconPosition =
      this.swipeSDKConfig?.swipeGames?.iconPosition || 'right';
    this.iconPositionClass = `game-launcher-at-${this.iconPosition}-position`;
    this.isClickEvent = false;
    this.currentY = 0;
    this.isArrowIconsFeature = isTenantFeatureEnabled([
      TenantFeature.ARROW_ICONS,
    ]);
    this.isTransitioning = false;
    this.lastTapTime = 0;
    this.tapTimeout = null;
    SwipeCommonMethods.applyPositionClasses(this.iconPosition);
    this.setupTouchListeners();
    idleCollapse.start();
  }

  private setupTouchListeners(): void {
    if (!this.swipe_control) return;

    this.swipe_control.addEventListener('touchstart', (e) =>
      this.swipeHandleTouchStart(e),
    );
    this.swipe_control.addEventListener('touchmove', (e) =>
      this.swipeHandleTouchMove(e),
    );
    this.swipe_control.addEventListener('touchend', (e) =>
      this.swipeHandleTouchFinish(e),
    );

    globalThis.addEventListener('resize', () => {
      const { swipe_control, swipeHandle } = getElementSelectors();
      this.swipeSDKConfig = getSDKConfig();
      this.swipe_control = swipe_control;
      this.swipeHandle = swipeHandle;
      this.pretX = 0;
      this.pretY = this.swipeSDKConfig?.swipeGames?.styles?.iconTop ?? 0;
      this.startClickPosY = 0;
      this.startClickPosX = 0;
      this.screenProps = getSizeOfDevice();
      this.iconPosition =
        this.swipeSDKConfig?.swipeGames?.iconPosition || 'right';
      this.iconPositionClass = `game-launcher-at-${this.iconPosition}-position`;
      this.isClickEvent = false;
    });
  }

  private swipeHandleTouchStart(e: TouchEvent): void {
    // Block if transition is in progress
    if (this.isTransitioning) {
      if (e.cancelable) {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }

    if (e.cancelable) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!this.swipe_control || !this.swipeHandle) return;

    idleCollapse.expand();
    SwipeCommonMethods.restartArrowAnimation(this.swipe_control);
    idleCollapse.pauseCountdown();

    const initialSwipePosition_Y =
      (this.screenProps.clientHeight * this.pretY) / 100;
    let swipeTrasform_Y = e.touches[0].clientY - initialSwipePosition_Y;
    swipeTrasform_Y = swipeTrasform_Y > 50 ? swipeTrasform_Y : 0;
    this.swipe_control.style.transform = `translate(0px, 0px)`;
    this.dragStartTop =
      Number.parseFloat(globalThis.getComputedStyle(this.swipe_control).top) ||
      0;
    this.startClickPosY = swipeTrasform_Y;
    this.currentY = this.swipeHandle.classList.contains(this.iconPositionClass)
      ? e.touches[0].clientY
      : this.startClickPosY + e.touches[0].clientY;
    this.startClickPosX = e.touches[0].clientX;
    // this.swipeErrorMsg!.style.display = 'none';
    this.isClickEvent = true;
  }

  private swipeHandleTouchMove(e: TouchEvent): void {
    if (this.isTransitioning) {
      if (e.cancelable) e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (e.cancelable) e.preventDefault();
    e.stopPropagation();

    if (!this.swipe_control || !this.swipeHandle) return;

    if (
      Math.abs(e.touches[0].clientY) > globalThis.innerHeight - 50 ||
      Math.abs(e.touches[0].clientY) < 50
    ) {
      return;
    }

    const diffY = this.startClickPosY + e.touches[0].clientY - this.currentY;
    this.pretY = this.dragStartTop + diffY;

    const diffX = e.touches[0].clientX - this.startClickPosX;
    const isAtIconPosition = this.swipeHandle.classList.contains(
      this.iconPositionClass,
    );
    const maxTranslate = this.screenProps.clientWidth;

    const x = isAtIconPosition
      ? this.iconPosition === 'left'
        ? Math.max(Math.min(diffX, 0), -maxTranslate)
        : Math.min(Math.max(diffX, 0), maxTranslate)
      : this.iconPosition === 'left'
        ? Math.min(Math.max(diffX, 0), maxTranslate)
        : Math.max(Math.min(diffX, 0), -maxTranslate);

    this.pretX = x;
    if (this.isArrowIconsFeature) {
      this.calculateSwipeAnimation(x, diffY, maxTranslate);
    } else {
      this.swipe_control.style.transform = `translate(0px, ${diffY}px)`;
      this.swipeHandle.style.transform = `translate(${x}px, 0px)`;
    }
    // globalThis.scrollTo(0, 1);
    if (Math.abs(x) > 5 || Math.abs(diffY) > 5) {
      this.isClickEvent = false;
    }
  }

  private swipeHandleTouchFinish(e: TouchEvent): void {
    if (this.isTransitioning) {
      if (e.cancelable) {
        e.preventDefault();
        e.stopPropagation();
      }
      return;
    }

    if (e.cancelable) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!this.swipe_control || !this.swipeHandle) return;

    idleCollapse.resumeCountdown();

    const allowGameSwipePerc =
      Math.abs(this.pretX / this.screenProps.clientWidth) * 100;
    if (!this.isClickEvent && allowGameSwipePerc < 40) {
      this.resetSwipePosition();
      return;
    }

    if (this.isClickEvent) {
      this.handleDoubleTapDetection();
      return;
    }

    this.executeSingleTapAction();
  }

  private handleDoubleTapDetection(): void {
    const currentTime = Date.now();
    const timeDiff = currentTime - this.lastTapTime;

    if (timeDiff < 300 && timeDiff > 0) {
      this.lastTapTime = 0;
      if (this.tapTimeout) {
        clearTimeout(this.tapTimeout);
        this.tapTimeout = null;
      }
      return;
    }

    this.lastTapTime = currentTime;
    if (this.tapTimeout) {
      clearTimeout(this.tapTimeout);
    }

    this.tapTimeout = setTimeout(() => {
      this.executeSingleTapAction();
    }, 50);
  }

  private executeSingleTapAction(): void {
    const isIconPositionClass = this.swipeHandle!.classList.contains(
      this.iconPositionClass,
    );
    if (this.isArrowIconsFeature) {
      this.isTransitioning = true;
      this.handleExpandAnimation(isIconPositionClass);
    } else {
      this.swipeHandle!.classList.toggle(this.iconPositionClass);
      this.resetSwipePosition();
    }

    if (isIconPositionClass) {
      SwipeCommonMethods.handleSwipeClose(
        this.swipeSdkContainer,
        this.swipe_control,
        this.isArrowIconsFeature,
      );
    } else {
      SwipeCommonMethods.handleSwipeOpen(
        this.swipeHandle,
        this.swipeSdkContainer,
        this.swipe_control,
        this.isArrowIconsFeature,
      );
    }
  }

  private resetSwipePosition(): void {
    SwipeCommonMethods.resetSwipePosition(
      this.swipe_control,
      this.swipeHandle,
      this.pretY,
      this.screenProps,
      this.isArrowIconsFeature,
    );
  }

  private calculateSwipeAnimation(
    x: number,
    diffY: number,
    maxTranslate: number,
  ): void {
    SwipeCommonMethods.calculateSwipeAnimation(
      this.swipe_control,
      this.swipeHandle,
      x,
      diffY,
      maxTranslate,
      this.iconPositionClass,
    );
  }

  private handleExpandAnimation(isIconPositionClass: boolean): void {
    SwipeCommonMethods.handleExpandAnimation(
      this.swipe_control,
      this.swipeHandle,
      this.iconPositionClass,
      isIconPositionClass,
      {
        onComplete: () => {
          this.resetSwipePosition();
          this.isTransitioning = false;
        },
        opacityTransition: 'opacity 3s ease-out',
        width: '150vw',
        transitionDuration: '0.2s',
      },
    );
  }
}

export const touchSwipeActions = (): SwipeHandler => {
  return new SwipeHandler();
};
